import type { QueryFunctionContext } from '@tanstack/react-query';
import type { PreferredMultiple } from '../../selector';
import { gql } from 'graphql-request';
import type { CurrencyISO } from '@/constants/currencies';
import type { ISO2 } from '@/constants/countries';
import { request } from '@utilities/graphQLClient';
export interface Company {
  id: string;
  name: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  canonicalURL: string;
  exchangeCountryISO: ISO2;
  listingCurrencyISO: CurrencyISO;
  analysisValue: {
    marketCap: number | null;
    marketCapUSD: number | null;
    pe: number | null;
    pb: number | null;
    priceToSales: number | null;
    return1yAbs: number | null;
  };
  analysisFuture: {
    netIncome1Y: number | null;
    revenue1Y: number | null;
    netIncomeGrowth1Y: number | null;
    revenueGrowth1Y: number | null;
    netIncomeGrowthAnnual: number | null;
    revenueGrowthAnnual: number | null;
    forwardPe1Y: number | null;
    forwardPriceToSales1Y: number | null;
  };
}

interface Error {
  message: string;
}

interface QueryResponse {
  CompanyIndustryHistogramCompanies: Company[];
  errors?: Error[];
}

const companyFields = gql`
  fragment CompanyFields on Company {
    id
    name
    tickerSymbol
    uniqueSymbol
    canonicalURL
    exchangeCountryISO
    listingCurrencyISO
    analysisValue {
      marketCap
      marketCapUSD
      pe
      pb
      priceToSales
      return1yAbs
    }
    analysisFuture {
      netIncome1Y
      revenue1Y
      netIncomeGrowth1Y
      revenueGrowth1Y
      forwardPe1Y
      forwardPriceToSales1Y
      netIncomeGrowthAnnual
      revenueGrowthAnnual
    }
    country {
      iso2
      region {
        name
      }
    }
    primaryIndustry {
      id
    }
  }
`;

export function getQueryKey(
  preferredMultiple: PreferredMultiple,
  countryISO: string | null,
  region: string | null,
  min: number | null,
  max: number | null,
  industryId: number,
  limit: number
): [
  string,
  string,
  string | null,
  string | null,
  number | null,
  number | null,
  number,
  number,
] {
  return [
    'COMPANY_INDUSTRY_COMPANIES',
    preferredMultiple,
    countryISO,
    region,
    min,
    max,
    industryId,
    limit,
  ];
}

export const queryFn = async ({
  queryKey: [
    _keyof,
    preferredMultiple,
    countryISO,
    region,
    min,
    max,
    industryId,
    limit,
  ],
}: QueryFunctionContext<
  [
    string,
    string,
    string | null,
    string | null,
    number | null,
    number | null,
    number,
    number,
  ]
>): Promise<Company[]> => {
  try {
    const response = await request<QueryResponse>(
      gql`
        ${companyFields}
        query GetIndustryHistogramCompanies(
          $limit: Float!
          $industryId: Float!
          $preferredMultiple: String!
          $min: Float
          $max: Float
          $region: String
          $sortBy: String
          $sortOrder: String
          $countryISO: String
        ) {
          CompanyIndustryHistogramCompanies(
            limit: $limit
            industryID: $industryId
            preferredMultiple: $preferredMultiple
            min: $min
            max: $max
            region: $region
            countryISO: $countryISO
            sortBy: $sortBy
            sortOrder: $sortOrder
          ) {
            ...CompanyFields
          }
        }
      `,
      {
        limit,
        industryId,
        preferredMultiple: preferredMultiple.toLowerCase(),
        min,
        max,
        region,
        countryISO,
        sortBy: 'analysisValue.marketCap',
        sortOrder: 'DESC',
      }
    );
    const { errors } = response;
    if (errors && errors.length > 0) {
      console.log(errors[0].message);
      throw new Error(errors[0].message);
    }
    return response.CompanyIndustryHistogramCompanies;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
